import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtBudget, fmtDuree, fmtEUR, L } from "@/constants/labels";
import { NetPercu, NetPercuFourchette } from "@/components/commission/Commission";
import { Upload, FileText, Trash2, CheckCircle2, Lock, KeyRound, Eye, EyeOff } from "lucide-react";
import ContreOffreThread from "@/components/ContreOffreThread";
import { BESOIN_DOC_ACCEPT, BESOIN_DOC_FORMATS_HINT, docKind, VisibilityBadge } from "@/components/BesoinDocs";

function humanSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

function DocList({ docs, canDelete, onDelete, emptyText, category }) {
  return (
    <ul className="divide-y divide-[#E4E7EB]" data-testid={`docs-list-${category}`}>
      {docs.length === 0 && <li className="py-3 text-sm text-[#3E4C59]">{emptyText}</li>}
      {docs.map(d => (
        <li key={d.id} className="py-2 flex items-center gap-2 text-sm">
          <FileText size={16} className="text-[#1B3A5C] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#1F2933] truncate">{d.filename}</div>
            <div className="text-xs text-[#3E4C59]">{docKind(d.filename)} · {humanSize(d.size)} · déposé le {fmtDate(d.created_at)} <VisibilityBadge category={category} /></div>
          </div>
          <a href={`${BACKEND_URL}${d.signed_url}`} target="_blank" rel="noreferrer"
             className="btn-outline" data-testid={`doc-download-${d.id}`}>Télécharger</a>
          {canDelete && (
            <button onClick={() => onDelete(d.id)} className="btn-ghost text-[#C0392B]" aria-label="Supprimer"
                    data-testid={`doc-delete-${d.id}`}>
              <Trash2 size={16} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function UploadBox({ besoinId, category, disabled, onUploaded, maxCount, currentCount }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (currentCount >= maxCount) {
      toast.error(`Maximum ${maxCount} fichiers pour cette catégorie.`);
      e.target.value = "";
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (20 Mo maximum).");
      e.target.value = "";
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("category", category);
    fd.append("file", file);
    try {
      await api.post(`/besoins/${besoinId}/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pièce jointe déposée");
      onUploaded();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className={`btn-outline cursor-pointer ${disabled || busy ? "opacity-50 pointer-events-none" : ""}`}
           data-testid={`upload-${category}`}>
      <Upload size={16} /> {busy ? "Envoi…" : "Ajouter un document"}
      <input type="file" accept={BESOIN_DOC_ACCEPT} className="hidden" onChange={onFile} disabled={disabled || busy} />
    </label>
  );
}

export default function BesoinDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [cands, setCands] = useState([]);
  const [publicDocs, setPublicDocs] = useState([]);
  const [contractDocs, setContractDocs] = useState([]);
  const [msg, setMsg] = useState("");
  const [montantPropose, setMontantPropose] = useState("");
  const [busy, setBusy] = useState(false);
  // Accès chantier chiffré
  const [accesContent, setAccesContent] = useState("");
  const [accesRevealed, setAccesRevealed] = useState(false);
  const [accesLoaded, setAccesLoaded] = useState(false);
  const [accesUpdatedAt, setAccesUpdatedAt] = useState(null);
  const [accesBusy, setAccesBusy] = useState(false);
  const [accesError, setAccesError] = useState(null);

  const load = useCallback(async () => {
    const [b1, c1, dp] = await Promise.all([
      api.get(`/besoins/${id}`),
      api.get(`/candidatures`, { params: { besoin_id: id } }).catch(() => ({ data: [] })),
      api.get(`/besoins/${id}/docs`, { params: { category: "public" } }).catch(() => ({ data: [] })),
    ]);
    setB(b1.data);
    setCands(c1.data);
    setPublicDocs(dp.data);
    // Contract docs — only fetch if potentially authorized (avoid useless 403 in logs)
    if (b1.data.is_owner || b1.data.is_assigned || user?.role === "admin") {
      const dc = await api.get(`/besoins/${id}/docs`, { params: { category: "contract" } }).catch(() => ({ data: [] }));
      setContractDocs(dc.data);
    } else {
      setContractDocs([]);
    }
  }, [id, user]);
  useEffect(() => { load(); }, [load]);

  const candidater = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/candidatures", { besoin_id: id, message: msg, montant_propose_ht: montantPropose ? Number(montantPropose) : null });
      toast.success("Candidature envoyée"); setMsg(""); setMontantPropose(""); load();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const attribuer = async (prestataireId) => {
    if (!window.confirm(L.wording.besoinDetailConfirmAttribuer)) return;
    try {
      await api.post(`/besoins/${id}/attribuer`, { prestataire_id: prestataireId });
      toast.success(L.wording.besoinDetailAttribueSuccess);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Supprimer cette pièce jointe ?")) return;
    try {
      await api.delete(`/besoins/${id}/docs/${docId}`);
      toast.success("Supprimée"); load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  // ------ Accès chantier chiffré ------
  const loadAcces = async () => {
    setAccesBusy(true);
    setAccesError(null);
    try {
      const r = await api.get(`/besoins/${id}/acces`);
      setAccesContent(r.data.content || "");
      setAccesUpdatedAt(r.data.updated_at || null);
      setAccesLoaded(true);
    } catch (err) {
      if (err?.response?.status === 404) {
        setAccesContent("");
        setAccesUpdatedAt(null);
        setAccesLoaded(true);
      } else if (err?.response?.status === 403) {
        setAccesError(L.wording.besoinDetailAccesRefuse);
      } else {
        setAccesError(fmtError(err));
      }
    } finally {
      setAccesBusy(false);
    }
  };

  const saveAcces = async () => {
    setAccesBusy(true);
    try {
      await api.patch(`/besoins/${id}/acces`, { content: accesContent });
      toast.success("Accès chantier enregistré (chiffré)");
      await loadAcces();
      // Recharge le besoin pour rafraîchir has_acces_chantier
      const b1 = await api.get(`/besoins/${id}`);
      setB(b1.data);
    } catch (err) { toast.error(fmtError(err)); } finally { setAccesBusy(false); }
  };

  const clearAcces = async () => {
    if (!window.confirm("Effacer définitivement le bloc accès chantier ?")) return;
    setAccesBusy(true);
    try {
      await api.patch(`/besoins/${id}/acces`, { content: "" });
      toast.success("Accès chantier effacé");
      setAccesContent("");
      setAccesUpdatedAt(null);
      const b1 = await api.get(`/besoins/${id}`);
      setB(b1.data);
    } catch (err) { toast.error(fmtError(err)); } finally { setAccesBusy(false); }
  };

  const isOwner = b?.is_owner;
  const isAssigned = b?.is_assigned;
  const canSeeContract = isOwner || isAssigned || user?.role === "admin";
  const [reservedCount, setReservedCount] = useState(0);
  useEffect(() => { if (b && !canSeeContract) api.get(`/besoins/${id}/docs/summary`).then((r) => setReservedCount(r.data.reserved_count || 0)).catch(() => {}); }, [id, b, canSeeContract]);
  if (!b) return <div className="text-sm text-[#3E4C59]">{L.common.loading}</div>;

  return (
    <div className="max-w-3xl">
      <Link to="/app/chantiers" className="btn-ghost">← {L.common.back}</Link>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">{b.titre}</h1>
        {b.statut === "attribue" && <span className="badge-valid" data-testid="besoin-attribue-badge"><CheckCircle2 size={12} /> Attribué</span>}
      </div>
      <p className="text-sm text-[#3E4C59]">Publié par {b.entreprise_nom} • {fmtDate(b.created_at)}</p>

      <div className="card p-4 mt-4 space-y-2 text-sm">
        <div><b>Métier :</b> {[b.metier, b.specialite, b.sous_specialite].filter(Boolean).join(" • ")}</div>
        <div><b>Localisation :</b> {[b.rue, b.ville].filter(Boolean).join(", ")} ({b.code_postal})</div>
        <div><b>Budget :</b> {fmtBudget(b)}</div>
        {user?.role === "prestataire" && b.budget_type === "fourchette" && <div><NetPercuFourchette min={b.budget_min} max={b.budget_max} testid="besoin-net-fourchette" /></div>}
        {user?.role === "prestataire" && b.budget_type !== "fourchette" && b.budget_ht > 0 && <NetPercu montant={b.budget_ht} label={b.budget_type === "indicatif" ? "Budget indicatif" : "Budget annoncé"} testid="besoin-net-percu" />}
        <div><b>Durée :</b> {fmtDuree(b)}</div>
        <div><b>Démarrage :</b> {fmtDate(b.date_debut)}</div>
        <div><b>Échéance de livraison :</b> {fmtDate(b.date_fin)}</div>
        <p className="pt-2 border-t border-[#E4E7EB] text-[#1F2933] whitespace-pre-wrap">{b.description}</p>
      </div>

      {/* Pièces jointes publiques */}
      <div className="card p-4 mt-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-[#1F2933]">Documents du chantier</h2>
          {isOwner && (
            <UploadBox besoinId={id} category="public" onUploaded={load}
                       maxCount={15 - contractDocs.length} currentCount={publicDocs.length} />
          )}
        </div>
        <p className="text-xs text-[#3E4C59] mt-1">Visibles par les artisans intéressés (comptes vérifiés) : descriptif, quantitatif, photos. {isOwner && BESOIN_DOC_FORMATS_HINT + ". Photos nettoyées de leurs données GPS."}</p>
        <div className="mt-3">
          <DocList docs={publicDocs} canDelete={isOwner} onDelete={deleteDoc}
                   emptyText={isOwner ? "Aucun document déposé pour le moment." : "Aucun document partagé sur ce chantier."}
                   category="public" />
        </div>
      </div>

      {/* Documents réservés au sous-traitant retenu */}
      {!canSeeContract && reservedCount > 0 && (
        <div className="card p-3 mt-4 text-sm text-[#1F2933] flex items-start gap-2 border-l-4 border-[#E8710A]" data-testid="docs-reserved-notice">
          <Lock size={16} className="text-[#E8710A] shrink-0 mt-0.5" />
          <span>{reservedCount} document{reservedCount > 1 ? "s" : ""} supplémentaire{reservedCount > 1 ? "s" : ""} (plans détaillés, accès, coordonnées) {reservedCount > 1 ? "seront accessibles" : "sera accessible"} au sous-traitant retenu après attribution.</span>
        </div>
      )}
      {canSeeContract ? (
        <div className="card p-4 mt-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-[#1F2933] inline-flex items-center gap-2">
              <Lock size={16} className="text-[#E8710A]" /> Réservé au sous-traitant retenu
            </h2>
            {isOwner && (
              <UploadBox besoinId={id} category="contract" onUploaded={load}
                         maxCount={15 - publicDocs.length} currentCount={contractDocs.length} />
            )}
          </div>
          <p className="text-xs text-[#3E4C59] mt-1">Plans détaillés, coordonnées du client final, codes d'accès — accessibles uniquement après attribution.</p>
          <div className="mt-3">
            <DocList docs={contractDocs} canDelete={isOwner} onDelete={deleteDoc}
                     emptyText={isOwner ? "Aucun document contractuel déposé." : "Aucun document contractuel."}
                     category="contract" />
          </div>
        </div>
      ) : (
        <div className="card p-4 mt-4 border-dashed" data-testid="contract-locked">
          <div className="text-sm text-[#3E4C59] inline-flex items-center gap-2">
            <Lock size={14} /> {L.wording.besoinDetailContractLockedNotice}
          </div>
        </div>
      )}

      {/* Accès chantier chiffré — bloc sensible */}
      {(isOwner || isAssigned || user?.role === "admin") && (
        <div className="card p-4 mt-4 border-l-4 border-l-[#E8710A]" data-testid="acces-chantier-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-[#1F2933] inline-flex items-center gap-2">
              <KeyRound size={16} className="text-[#E8710A]" /> Accès chantier
              <span className="badge-muted text-[10px]">Chiffré au repos</span>
            </h2>
            {!accesLoaded && !accesError && (
              <button onClick={loadAcces} disabled={accesBusy} className="btn-outline" data-testid="acces-reveal-btn">
                <Eye size={14} /> {accesBusy ? "…" : (isOwner ? "Consulter / modifier" : "Révéler l'accès")}
              </button>
            )}
            {accesLoaded && !isOwner && (
              <button onClick={() => { setAccesRevealed(v => !v); }} className="btn-ghost" data-testid="acces-toggle-btn">
                {accesRevealed ? <><EyeOff size={14} /> Masquer</> : <><Eye size={14} /> Afficher</>}
              </button>
            )}
          </div>
          <p className="text-xs text-[#3E4C59] mt-1">
            {isOwner
              ? L.wording.besoinDetailAccesChantierHint
              : "Contenu confidentiel fourni par le donneur d'ordre. Chaque consultation est journalisée. L'accès sera automatiquement révoqué à la clôture de la mission."}
          </p>

          {accesError && (
            <div className="mt-3 text-sm text-[#C0392B]" data-testid="acces-error">{accesError}</div>
          )}

          {accesLoaded && !accesError && (
            <div className="mt-3 space-y-2">
              {isOwner ? (
                <>
                  <textarea
                    rows={5}
                    maxLength={2000}
                    className="textarea font-mono text-sm"
                    placeholder="Ex. Digicode portail 1234A. Clés dans la boîte à clés à droite (code 5678). Interphone : Batiment B – 3e étage."
                    value={accesContent}
                    onChange={(e) => setAccesContent(e.target.value)}
                    data-testid="acces-textarea"
                  />
                  <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-[#3E4C59]">
                    <span>{accesContent.length} / 2000 caractères {accesUpdatedAt && `• dernière mise à jour ${fmtDate(accesUpdatedAt)}`}</span>
                    <div className="flex gap-2">
                      {accesUpdatedAt && (
                        <button onClick={clearAcces} disabled={accesBusy} className="btn-ghost text-[#C0392B]" data-testid="acces-clear-btn">
                          <Trash2 size={14} /> Effacer
                        </button>
                      )}
                      <button onClick={saveAcces} disabled={accesBusy} className="btn-primary" data-testid="acces-save-btn">
                        {accesBusy ? "…" : "Enregistrer (chiffré)"}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {accesContent ? (
                    <pre className={`whitespace-pre-wrap font-mono text-sm p-3 bg-[#F5F6F7] rounded-[4px] border border-[#E4E7EB] ${accesRevealed ? "" : "blur-sm select-none"}`} data-testid="acces-content">
                      {accesContent}
                    </pre>
                  ) : (
                    <div className="text-sm text-[#3E4C59] italic" data-testid="acces-empty">Aucun accès chantier renseigné par le donneur d'ordre.</div>
                  )}
                  {accesUpdatedAt && (
                    <div className="text-xs text-[#3E4C59]">Mis à jour le {fmtDate(accesUpdatedAt)}</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Formulaire de candidature (prestataire non retenu, besoin ouvert, PAS son propre besoin) */}
      {user?.role === "prestataire" && b.statut === "ouvert" && !isAssigned && !isOwner && b.entreprise_id !== user?.id && (
        <form onSubmit={candidater} className="card p-4 mt-4">
          <h2 className="text-lg font-semibold text-[#1F2933]">Candidater</h2>
          <label className="label mt-2">Message</label>
          <textarea required rows={4} className="textarea" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="cand-message" />
          <label className="label mt-2">Montant que vous proposez (€ HT) <span className="font-normal text-[#3E4C59]">facultatif</span></label>
          <input type="number" min="0" step="50" className="input" value={montantPropose} onChange={(e) => setMontantPropose(e.target.value)} placeholder={b.budget_ht ? String(b.budget_ht) : ""} data-testid="cand-montant" />
          <div className="mt-2"><NetPercu montant={montantPropose || b.budget_ht} label={montantPropose ? "Votre proposition" : "Budget annoncé"} testid="cand-net-percu" /></div>
          <button disabled={busy} className="btn-primary mt-2" data-testid="cand-submit">{busy ? "…" : "Envoyer ma candidature"}</button>
        </form>
      )}
      {user?.role === "prestataire" && (isOwner || b.entreprise_id === user?.id) && (
        <div className="card p-4 mt-4 border-dashed" data-testid="cand-self-blocked">
          <div className="text-sm text-[#3E4C59]">
            Ce besoin a été publié par votre compte (mode donneur d'ordre). Vous ne pouvez pas y candidater
            avec votre casquette prestataire. Basculez en mode donneur d'ordre depuis la barre latérale
            pour le gérer.
          </div>
        </div>
      )}

      {/* Liste des candidatures + bouton attribuer (owner/admin) */}
      {(isOwner || user?.role === "admin") && (
        <div className="card mt-4 overflow-x-auto">
          <div className="p-3 font-semibold border-b border-[#E4E7EB]">Candidatures reçues ({cands.length})</div>
          <table className="table-compact w-full">
            <thead><tr><th>{L.wording.besoinDetailCandidatureTh}</th><th>Message</th><th>Statut</th><th>Reçue</th><th></th></tr></thead>
            <tbody>
              {cands.length === 0 && <tr><td colSpan={5} className="text-center text-[#3E4C59]">Aucune candidature</td></tr>}
              {cands.map(c => (
                <React.Fragment key={c.id}>
                <tr>
                  <td className="font-semibold">{c.prestataire_nom}</td>
                  <td className="max-w-md truncate">{c.message}{c.montant_propose_ht ? <span className="block text-xs text-[#3E4C59]">Proposition : {fmtEUR(c.montant_propose_ht)} HT</span> : null}</td>
                  <td>
                    {c.statut === "acceptee" && <span className="badge-valid">Acceptée</span>}
                    {c.statut === "refusee" && <span className="badge-danger">Refusée</span>}
                    {c.statut === "envoyee" && <span className="badge-muted">En attente</span>}
                  </td>
                  <td>{fmtDate(c.created_at)}</td>
                  <td className="flex gap-1">
                    <Link to={`/app/prestataires/${c.prestataire_id}`} className="btn-outline">Profil</Link>
                    {isOwner && b.statut !== "attribue" && c.statut === "envoyee" && (
                      <button onClick={() => attribuer(c.prestataire_id)} className="btn-primary"
                              data-testid={`attribuer-${c.prestataire_id}`}>Attribuer</button>
                    )}
                  </td>
                </tr>
                {/* Négociation par contre-offre — visible aux 2 rôles + admin */}
                {b.statut !== "attribue" && (
                  <tr>
                    <td colSpan={5} className="p-2 bg-[#F5F6F7]">
                      <ContreOffreThread
                        candidature={c}
                        role={user?.role}
                        currentUserId={user?.id}
                        onChanged={load}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
